import { articlesData } from "@/pages/article/articlesData";

export const SITE_NAME = "AllChinaBuy Spreadsheet Hub";
export const SITE_IMAGE =
  "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/49750b7389edabeda6497a215e0702e5.png";

export const LANGUAGES = ["en", "fr", "da", "de"];

export const DEFAULT_SEO = {
  title: "AllChinaBuy Spreadsheet Hub — Product Finds & Buying Guides",
  description:
    "Your independent resource hub for AllChinaBuy spreadsheet resources, product finds, buying guides, shipping information, and QC tips. Explore organized categories and guides in English, French, Danish, and German.",
  keywords:
    "AllChinaBuy, AllChinaBuy Spreadsheet, AllChinaBuy review, AllChinaBuy shipping, buying guides, product finds, shopping agent",
};

export function getSiteUrl(): string {
  const envUrl = import.meta.env.VITE_SITE_URL as string | undefined;
  if (envUrl) return envUrl.replace(/\/+$/, "");
  if (typeof window !== "undefined") return window.location.origin;
  return "";
}

export function getBasePath(): string {
  return typeof __BASE_PATH__ !== "undefined" && __BASE_PATH__ !== "/"
    ? __BASE_PATH__
    : "";
}

// Maps URL slug to the article data key.
export const ARTICLE_SLUGS: Record<string, string> = {
  "allchinabuy-spreadsheet": "spreadsheet",
  "allchinabuy-review": "review",
  "allchinabuy-shipping": "shipping",
  "allchinabuy-coupon": "coupon",
  "allchinabuy-warehouse": "warehouse",
  "allchinabuy-qc": "qc",
  "allchinabuy-safety": "safety",
  "allchinabuy-finds": "finds",
  "allchinabuy-products": "products",
  "allchinabuy-alternatives": "alternatives",
  "allchinabuy-vs-superbuy": "vs-superbuy",
  "allchinabuy-vs-sugargoo": "vs-sugargoo",
  "allchinabuy-vs-pandabuy": "vs-pandabuy",
  "allchinabuy-comparison": "comparison",
  "allchinabuy-spreadsheet-guide": "spreadsheet-guide",
  "allchinabuy-how-to-order": "how-to-order",
  "allchinabuy-beginner-guide": "beginner",
  "allchinabuy-faq": "faq",
  about: "about",
  contact: "contact",
  privacy: "privacy",
  terms: "terms",
  faq: "faq",
};

export interface ResolvedSeo {
  title: string;
  description: string;
  keywords: string;
  image: string;
  type: "website" | "article";
  canonicalPath: string;
  articleId?: string;
}

export function parsePath(pathname: string): { lang: string; page: string } {
  const parts = pathname.split("/").filter(Boolean);
  if (parts.length === 0) return { lang: "", page: "" };
  if (LANGUAGES.includes(parts[0])) {
    return { lang: parts[0], page: parts.slice(1).join("/") };
  }
  return { lang: "", page: parts.join("/") };
}

export function resolveSeo(pathname: string): ResolvedSeo {
  const { lang, page } = parsePath(pathname);
  const base = getBasePath();

  // Homepage (root and language home)
  if (page === "") {
    const canonicalPath = lang ? `${base}/${lang}/` : `${base}/`;
    return {
      title: DEFAULT_SEO.title,
      description: DEFAULT_SEO.description,
      keywords: DEFAULT_SEO.keywords,
      image: SITE_IMAGE,
      type: "website",
      canonicalPath,
    };
  }

  // Products directory page
  if (page === "products") {
    return {
      title: "AllChinaBuy Products — Category Directory & Buying Links",
      description:
        "Browse the complete AllChinaBuy product directory organized by category: shoes, hoodies, T-shirts, jackets, accessories and more. Each product links directly to the main website for purchase.",
      keywords:
        "AllChinaBuy products, AllChinaBuy finds, AllChinaBuy Spreadsheet, product directory, shopping",
      image: SITE_IMAGE,
      type: "website",
      canonicalPath: `${base}/${lang}/products`,
    };
  }

  // Article pages
  const articleId = ARTICLE_SLUGS[page];
  if (articleId && articlesData[articleId]) {
    const article = articlesData[articleId];
    return {
      title: article.metaTitle,
      description: article.metaDesc,
      keywords: `AllChinaBuy, AllChinaBuy Spreadsheet, ${article.h1}`,
      image: SITE_IMAGE,
      type: "article",
      canonicalPath: `${base}/${lang}/${page}`,
      articleId,
    };
  }

  // Fallback (unknown route)
  return {
    title: DEFAULT_SEO.title,
    description: DEFAULT_SEO.description,
    keywords: DEFAULT_SEO.keywords,
    image: SITE_IMAGE,
    type: "website",
    canonicalPath: `${base}${pathname}`,
  };
}